import { StorageAdapter } from '../storage/StorageAdapter'
import { rowsOf } from '../storage/rows'
import { classify, MAX_RETRIES, backoffFor } from './classify'
import { dispatch } from './endpoints'
import { dbg, dbgWarn, dbgError } from '../logger'
import { getOfflineUserId } from '../session'

/**
 * outbox.js — el motor de DRAIN del push (PUSH-01/02/03).
 *
 * Drena contra SQLite via la costura `db` inyectable (default = StorageAdapter, espejando la
 * ergonomia `{ db = StorageAdapter }` de F2). STOR-05: el unico acceso a SQL es via el
 * `db` inyectable. La red SOLO se toca en endpoints/handlers via la costura transport.js.
 *
 * Maquina de estados LOCKED (vocabulario v2, NUNCA v1 queued/syncing/failed):
 *   pending -> in_flight -> done | dead
 * Backoff [1s,3s,10s,30s,2m,10m,1h], MAX_RETRIES=10. El dead-letter NO bloquea la cola: el SELECT
 * filtra por status='pending', asi que una fila `dead` es invisible al drain para siempre (sin
 * head-of-line block).
 */

const BATCH_SIZE = 20
const AUTH_RETRY_MS = 5000

// Re-entrancy guard (JS es single-threaded -> basta un boolean para evitar ticks solapados).
let draining = false
// Pausa por 401: el worker no drena hasta re-auth; las filas se reencolan con AUTH_RETRY_MS para
// que reintenten incluso sin un resume explicito.
let authPaused = false

export function resumeAuth () { authPaused = false }
export function isAuthPaused () { return authPaused }
export function setAuthPaused (v) { authPaused = !!v }

/**
 * Drena la cola: procesa hasta BATCH_SIZE filas LISTAS, en orden FIFO por seq.
 *
 * @param {{ db?: object }} opts  ejecutor SQL inyectable (default = StorageAdapter).
 */
export async function drain ({ db = StorageAdapter } = {}) {
  if (draining || authPaused) return
  draining = true
  try {
    // FILTRO POR USUARIO (device compartido): solo se drenan las ops de ESTE usuario (o legacy NULL).
    // Si el usuario es desconocido (uid null: tests / antes del login), se DEGRADA a sin filtro para
    // NO bloquear nunca la sync por no conocer al dueño (comportamiento previo).
    const uid = getOfflineUserId()
    const userClause = uid != null ? ' AND (user_id IS NULL OR user_id=?)' : ''
    // LOOP por LOTES hasta vaciar las filas DUE: sin esto el drain subía a lo más BATCH_SIZE (20) por
    // tick (30s) = ~40 ops/min; una semana offline con cientos de fotos tardaba >15 min en drenar con
    // red perfecta. El CLAIM es idempotente, así que iterar mientras queden filas due es seguro. Las
    // filas que fallan reciben backoff (next_attempt_at futuro) y salen del SELECT -> el loop converge.
    // Backstop MAX_BATCHES contra un loop patológico. [review Hugo]
    const MAX_BATCHES = 1000
    // RELOJ CONGELADO del drain [review Hugo #12]: el corte de "due" se captura UNA vez antes del loop.
    // Si se recalculara Date.now() por batch, un 429 con Retry-After:0 (Laravel ThrottleRequests emite 0
    // al filo de la ventana) fija next_attempt_at=now y la fila volvería DUE en el batch siguiente ->
    // re-despacho inmediato hasta MAX_BATCHES × BATCH_SIZE (= 20.000) contra un backend que pide parar.
    // Con startedAt fijo, cualquier reagenda cae en un Date.now() posterior, sale del SELECT y espera al
    // próximo tick. (No es un spin de CPU —cada processRow awaitea un HTTP real— pero sí una ráfaga.)
    const startedAt = Date.now()
    for (let batch = 0; batch < MAX_BATCHES; batch++) {
      if (authPaused) break // un 401 a media tanda pausa el resto
      // FIFO por seq; solo filas pending y vencidas al arrancar el drain. El filtro status='pending' es
      // exactamente lo que hace al dead-letter NO-bloqueante: las filas `dead` jamas reaparecen.
      const result = await db.query(
        `SELECT * FROM outbox WHERE status='pending' AND next_attempt_at<=?${userClause} ORDER BY seq ASC LIMIT ?`,
        uid != null ? [startedAt, uid, BATCH_SIZE] : [startedAt, BATCH_SIZE]
      )
      const rows = rowsOf(result)
      if (!rows.length) break // nada más due -> fin del drain
      if (batch === 0) dbg('sync', `drain: ${rows.length}${rows.length === BATCH_SIZE ? '+' : ''} fila(s) pending por subir`)
      for (const row of rows) {
        if (authPaused) break
        await processRow(row, { db })
      }
    }
  } finally {
    draining = false
  }
}

/**
 * Procesa UNA fila: la reclama durablemente (pending -> in_flight), la despacha y aplica
 * onSuccess/onError. El `AND status='pending'` del CLAIM lo hace idempotente (reclama exactamente
 * una vez) y deja la marca durable in_flight para que recoverInFlight() la rescate tras un crash.
 */
async function processRow (row, { db = StorageAdapter } = {}) {
  await db.run(
    "UPDATE outbox SET status='in_flight' WHERE seq=? AND status='pending'",
    [row.seq],
    true
  )
  try {
    dbg('sync', `→ subiendo op=${row.op} seq=${row.seq} (entity=${row.entity})`)
    const result = await dispatch(row)
    await onSuccess(row, result, { db })
    dbg('sync', `✓ OK op=${row.op} seq=${row.seq}`)
  } catch (err) {
    await onError(row, err, { db })
  }
}

/**
 * onSuccess (version minima 04-01). Contrato commitSuccess LOCKED (PUSH-04 seam): la PRIMERA linea
 * corta-circuito si el handler ya hizo el success-write atomico — los handlers de 04-02 llaman
 * commitSuccess(...) y devuelven { committed:true }, asi que onSuccess NO debe re-escribir (ni
 * pisar la fila ya finalizada). Solo cuando result.committed es falsy (ej. el gate de 04-01 con
 * ops fake que no commitean) onSuccess corre su propio flip a status='done'.
 *
 * FRONTERA (PUSH-04): el success-write ATOMICO de id_mapping + meta lo agrega el plan 04-02 (es
 * dueño de la transaccion de exito via commitSuccess). Este onSuccess solo flipa status='done'
 * (camino no-committed) para que el drain sea completo y testeable; 04-02 NO necesita reescribirlo:
 * el guard `result.committed` de arriba es la costura PERMANENTE. Si un result trae server_id, NO
 * escribir id_mapping aqui (eso es la tx atomica de 04-02).
 */
async function onSuccess (row, result, { db = StorageAdapter } = {}) {
  if (result && result.committed) return // el handler ya hizo el commit atomico; no double-write
  await db.run(
    "UPDATE outbox SET status='done', done_at=?, last_error=NULL WHERE seq=? AND status='in_flight'",
    [Date.now(), row.seq],
    true
  )
}

/**
 * onError: clasifica el error (PURO) y aplica la transicion de estado correspondiente.
 *
 * CASO transitorio CRITICO (child-before-parent): un handler puede LANZAR un Error plano (sin
 * status) cuando un server_id esta sin resolver (el create padre aun no drena). classify devuelve
 * `retry` para status null -> backoff retry, NUNCA dead. FIFO por seq garantiza que el create padre
 * esta antes en la cola y drena primero. (04-02 lanza esto; 04-01 solo lo clasifica como retry.)
 */
async function onError (row, err, { db = StorageAdapter } = {}) {
  const outcome = classify(err, { op: row.op })
  const message = (err && (err.message || err.statusText)) || 'unknown'
  if (outcome.kind === 'dead') dbgError('sync', `✗ DEAD-LETTER op=${row.op} seq=${row.seq}: ${message}`, err)
  else if (outcome.kind !== 'success' && outcome.kind !== 'done') {
    dbgWarn('sync', `✗ ${outcome.kind} op=${row.op} seq=${row.seq}: ${message}`)
  }

  switch (outcome.kind) {
    case 'success':
    case 'done':
      // 403 task.create / 404 delete (o un done que llego por la rama de error): tratar como done.
      await db.run(
        "UPDATE outbox SET status='done', done_at=?, last_error=NULL WHERE seq=? AND status='in_flight'",
        [Date.now(), row.seq],
        true
      )
      return

    case 'reauth':
      // 401: pausar el worker y reencolar con backoff corto. NUNCA dead-letter.
      authPaused = true
      await db.run(
        "UPDATE outbox SET status='pending', next_attempt_at=?, last_error='401 auth' WHERE seq=? AND status='in_flight'",
        [Date.now() + AUTH_RETRY_MS, row.seq],
        true
      )
      return

    case 'throttle':
      // 429 (respeta Retry-After) O espera de dependencia interna (waitingDependency): reagenda
      // SIN consumir retries. last_error = mensaje real (antes '429 throttled' fijo).
      await db.run(
        "UPDATE outbox SET status='pending', next_attempt_at=?, last_error=? WHERE seq=? AND status='in_flight'",
        [Date.now() + outcome.retryAfterMs, `${message}`, row.seq],
        true
      )
      return

    case 'dead':
      // 4xx permanente (incl. 422, 403-delete): dead-letter. La cola sigue drenando (el SELECT
      // filtra status='pending', asi que esta fila ya no reaparece).
      await db.run(
        'UPDATE outbox SET status=\'dead\', last_error=? WHERE seq=? AND status=\'in_flight\'',
        [`${message}`, row.seq],
        true
      )
      return

    case 'retry':
    default: {
      // 5xx / red / status-null-transitorio: backoff exponencial; dead-letter tras MAX_RETRIES.
      const retries = (row.retries || 0) + 1
      if (retries > MAX_RETRIES) {
        await db.run(
          'UPDATE outbox SET status=\'dead\', retries=?, last_error=? WHERE seq=? AND status=\'in_flight\'',
          [retries, `${message}`, row.seq],
          true
        )
        return
      }
      // backoff indexado por el intento 0-based (retries-1): el 1er reintento espera BACKOFF[0]=1s,
      // el 2do BACKOFF[1]=3s, ... saturando en BACKOFF[last]=1h. (backoffFor satura solo.)
      await db.run(
        'UPDATE outbox SET status=\'pending\', retries=?, next_attempt_at=?, last_error=? WHERE seq=? AND status=\'in_flight\'',
        [retries, Date.now() + backoffFor(retries - 1), `${message}`, row.seq],
        true
      )
    }
  }
}
